import { AxiosResponse } from 'axios'
import i18n from 'i18next'
import get from 'lodash/get'
import has from 'lodash/has'

import { SignupDataFormatted } from 'helpers/params'
import { UserMfaSettings } from 'types'
import { Login, Tokens } from 'types/resources/auth'

import { unscopedApiClient } from '../../unscopedApiClient'

const errorInfo = (errorName: string): string => {
    return get(
        {
            ParamValidationError: i18n.t(
                'auth:errors.ParamValidationError',
                'Invalid parameter.'
            ),
            InvalidParameterException: i18n.t(
                'auth:errors.InvalidParameterException',
                'Invalid parameter.'
            ),
            TooManyFailedAttemptsException: i18n.t(
                'auth:errors.TooManyFailedAttemptsException',
                'Too many failed attempts.'
            ),
            ExpiredCodeException: i18n.t(
                'auth:errors.ExpiredCodeException',
                'Code has expired.'
            ),
            CodeMismatchException: i18n.t(
                'auth:errors.CodeMismatchException',
                'Code is incorrect.'
            ),
            UsernameExistsException: i18n.t(
                'auth:errors.UsernameExistsException',
                'User already exists.'
            ),
            UserNotFoundException: i18n.t(
                'auth:errors.UserNotFoundException',
                'User not found.'
            ),
            InvalidPasswordException: i18n.t(
                'auth:errors.InvalidPasswordException',
                'Provided password cannot be used for security reasons.'
            ),
            NotAuthorizedException: i18n.t(
                'auth:errors.NotAuthorizedException',
                'Invalid credentials were provided.'
            ),
            LimitExceededException: i18n.t(
                'auth:errors.LimitExceededException',
                'Attempt limit exceeded, please try again later.'
            ),
        },
        errorName
    )
}

const handleAuthResponse = (response: AxiosResponse): void => {
    if (
        (response.status === 401 &&
            has(response, ['data', 'code']) &&
            response.data.code === 'SessionExpired') ||
        (response.status === 302 &&
            has(response, ['data', 'code']) &&
            response.data.code === 'SmsChallenge')
    ) {
        return
    }
    if (response.status !== 200) {
        const defaultErr = i18n.t('auth:errors.UnknownError', 'Unknown error.')
        let errMsg = ''

        if (has(response, ['data', 'error_description'])) {
            errMsg = get(response, ['data', 'error_description'])
        } else if (has(response, ['data', 'error'])) {
            errMsg = get(response, ['data', 'error'])
        } else if (has(response, 'data')) {
            errMsg = errorInfo(response.data)
        }

        throw new Error(errMsg ?? defaultErr)
    }
}

export const login = async (
    username: string,
    password: string
): Promise<AxiosResponse<Login>> => {
    const response = await unscopedApiClient.post(
        `/oauth/token/`,
        {
            grant_type: 'password',
            username,
            password,
        },
        {
            auth: {
                username: CEREBRO_OAUTH_CLIENT_ID,
                password: CEREBRO_OAUTH_CLIENT_SECRET,
            },
        }
    )
    handleAuthResponse(response)
    return response
}

export const loginMfa = async (
    email: string,
    password: string,
    tokens: Tokens,
    code: string
): Promise<AxiosResponse<Login>> => {
    const response = await unscopedApiClient.post(
        `/oauth/token/`,
        {
            grant_type: 'password',
            username: email,
            password,
            tokens,
            code,
        },
        {
            headers: {
                'Content-Type': 'application/json',
            },
            auth: {
                username: CEREBRO_OAUTH_CLIENT_ID,
                password: CEREBRO_OAUTH_CLIENT_SECRET,
            },
        }
    )
    handleAuthResponse(response)
    return response
}

const REVOKE_TIMEOUT_MS = 2000

/**
 * Server-side end of the session (RFC 7009). Revoking the refresh token also
 * drops its access token, so one call covers both.
 */
export const revokeRefreshToken = async (
    refreshToken: string
): Promise<void> => {
    await unscopedApiClient.post(
        `/oauth/revoke_token/`,
        {
            token: refreshToken,
            token_type_hint: 'refresh_token',
        },
        {
            auth: {
                username: CEREBRO_OAUTH_CLIENT_ID,
                password: CEREBRO_OAUTH_CLIENT_SECRET,
            },
            timeout: REVOKE_TIMEOUT_MS,
        }
    )
}

export const signUp = async (
    userData: SignupDataFormatted
): Promise<AxiosResponse<unknown>> => {
    const response = await unscopedApiClient.post(`/api/auth/sign_up/`, {
        email: userData.username,
        password: userData.password,
        name: userData.attributes.name ?? '',
        token: userData.token,
    })
    handleAuthResponse(response)
    return response
}

export const checkUserNotConfirmed = async (
    email: string
): Promise<boolean> => {
    const response = await unscopedApiClient.post(`/api/auth/authenticate/`, {
        email,
        password: 'dummyPassword',
    })

    try {
        handleAuthResponse(response)
    } catch (e: any) {
        if (e.message === 'UserNotConfirmedException') {
            return true
        }
    }
    return false
}

export const resendSignUpCode = async (email: string): Promise<void> => {
    const response = await unscopedApiClient.post(
        `/api/auth/resend_confirmation_code/`,
        {
            email,
        }
    )
    handleAuthResponse(response)
}

export const confirmSignUp = async (
    email: string,
    code: string
): Promise<void> => {
    const response = await unscopedApiClient.post(
        `/api/auth/confirm_sign_up/`,
        {
            email,
            code,
        }
    )
    handleAuthResponse(response)
}

export const sendResetEmail = async (email: string): Promise<void> => {
    const response = await unscopedApiClient.post(
        `/api/auth/request_reset_password/`,
        {
            email,
        }
    )
    handleAuthResponse(response)
}

export const resetPassword = async (
    email: string,
    code: string,
    password: string
): Promise<void> => {
    const response = await unscopedApiClient.post(
        `/api/auth/submit_reset_password/`,
        {
            email,
            code,
            password,
        }
    )
    handleAuthResponse(response)
}

export const getUserMfaSettings = (): Promise<AxiosResponse<UserMfaSettings>> =>
    unscopedApiClient.get(`api/user_mfa_settings/`)

export const updateUserPhoneNumber = (
    phoneNumber: string,
    accessToken: string
): Promise<AxiosResponse> =>
    unscopedApiClient.put(`api/user_mfa_settings/phone_number/`, {
        phone_number: phoneNumber,
        access_token: accessToken,
    })

export const triggerUserPhoneNumberVerification = (
    accessToken: string
): Promise<AxiosResponse> =>
    unscopedApiClient.post(
        `api/user_mfa_settings/phone_number/verification/codes/`,
        {
            access_token: accessToken,
        }
    )

export const verifyUserPhoneNumber = (
    smsCode: string,
    accessToken: string
): Promise<AxiosResponse> =>
    unscopedApiClient.post(`api/user_mfa_settings/phone_number/verification/`, {
        sms_code: smsCode,
        access_token: accessToken,
    })

export const getMfaSettingsAccessToken = (
    email: string,
    password: string
): Promise<AxiosResponse> => {
    return unscopedApiClient.post(`api/user_mfa_settings/token/`, {
        email,
        password,
    })
}
